/**
 * Location form — one surface for creating and editing a venue-catalog
 * entry. Mounted via `show()`; closes itself by dispatching `close`.
 *
 * `within` offers only candidates `withinOptions` already excludes
 * self-reference and cycles from — error prevention by construction (H5)
 * rather than a post-hoc validation message.
 *
 * Delete goes through the deleteLocation door, which censuses all three
 * references a venue can carry — an event's own locationId, a ticket type's
 * room override, a room's parent `within` — inside the transaction that
 * would delete, and refuses with those counts. `usedByCount` stays as the
 * cheap early signal that greys the button out and says why, but it is no
 * longer the guard: it counts events alone, and the other two references
 * live in collections this form never loads.
 */

import { overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { confirm, show } from "@mhmo91/schmancy/overlay";
import { organizationPathPrefix } from "../workspace/organization.state.ts";
import "@mhmo91/schmancy/place-picker";
import type {
	SchmancyPlacePickerChangeEvent,
	SchmancyPlacePickerInputEvent,
} from "@mhmo91/schmancy/place-picker";
import { Dependents, Location } from "@momo/types";
import { html, type TemplateResult } from "lit";
import { property, state as stateDecorator } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import {
	filter,
	firstValueFrom,
	map,
	merge,
	share,
	take,
	takeUntil,
	timer,
} from "rxjs";
import { workspaceMapsApiKey } from "../workspace/workspace.state.ts";
import "../catalog/delete-refusal.view.ts";
import { LocalizeMixin } from "../i18n/localize.ts";
import { locationsState } from "../storefront/locations.state.ts";
import {
	type LocationWrite,
	locationsAction$,
	locationsPageView,
	locationsResult$,
	withinOptions,
} from "./locations.state.ts";

// Every IANA zone the runtime actually knows — native `Intl`, no curated list
// to keep in sync with the tz database's own updates.
const TIMEZONE_OPTIONS = Intl.supportedValuesOf("timeZone");

/** How long a save waits for its answer before saying it is unconfirmed —
 *  the bound the legal reads settle on. */
const SAVE_ANSWER_BOUND_MS = 15_000;

@schmancyElement("location-form")
export class LocationForm extends LocalizeMixin(SchmancyElement) {
	/** Empty means create; set means edit that location's own id. Overrides the inherited HTMLElement.id. */
	@property({ type: String }) override id = "";

	@stateDecorator() private name = "";
	@stateDecorator() private address = "";
	@stateDecorator() private lat: number | null = null;
	@stateDecorator() private lng: number | null = null;
	@stateDecorator() private url = "";
	@stateDecorator() private notes = "";
	@stateDecorator() private within = "";
	@stateDecorator() private timezone =
		Location.LocationSchema.shape.timezone.parse(undefined);
	@stateDecorator() private saving = false;
	@stateDecorator() private activeAction: "save" | "delete" | null = null;
	@stateDecorator() private failedAction: "save" | "delete" | null = null;
	/** The save went out and no answer came within the bound: it may still
	 *  land, so the form says so rather than calling it refused. */
	@stateDecorator() private saveUnconfirmed = false;

	override connectedCallback(): void {
		super.connectedCallback();
		const canonical = this.id
			? locationsState.value.find((l) => l.id === this.id)
			: undefined;
		if (!canonical) return;
		this.name = canonical.name;
		this.address = canonical.address;
		this.lat = canonical.geo?.lat ?? null;
		this.lng = canonical.geo?.lng ?? null;
		this.url = canonical.url ?? "";
		this.notes = canonical.notes ?? "";
		this.within = canonical.within ?? "";
		this.timezone = canonical.timezone;
	}

	private close(): void {
		this.dispatchEvent(
			new CustomEvent("close", { bubbles: true, composed: true }),
		);
	}

	private async save(): Promise<void> {
		if (this.saving) return;
		const name = this.name.trim();
		const address = this.address.trim();
		const timezone = this.timezone.trim();
		if (!name || !address || !timezone) return;

		const body: LocationWrite = {
			name,
			address,
			geo:
				this.lat != null &&
				this.lng != null &&
				Number.isFinite(this.lat) &&
				Number.isFinite(this.lng)
					? { lat: this.lat, lng: this.lng }
					: null,
			url: this.url.trim() || null,
			notes: this.notes.trim() || null,
			within: this.within || null,
			timezone,
		};

		this.saving = true;
		this.activeAction = "save";
		this.saveUnconfirmed = false;
		try {
			const requestId = crypto.randomUUID();
			const answer$ = locationsResult$.pipe(
				filter((r) => r.requestId === requestId),
				take(1),
				takeUntil(this.disconnecting),
				share(),
			);
			const first = firstValueFrom(
				merge(
					answer$,
					timer(SAVE_ANSWER_BOUND_MS).pipe(
						map(() => "unconfirmed" as const),
						takeUntil(answer$),
					),
				),
			);
			locationsAction$.next(
				this.id
					? { kind: "update", requestId, id: this.id, body }
					: { kind: "create", requestId, body },
			);
			const result = await first;
			if (result === "unconfirmed") {
				this.saveUnconfirmed = true;
				this.failedAction = "save";
				// The answer that comes after the bound still settles the form.
				answer$.subscribe((late) => {
					this.saveUnconfirmed = false;
					if (late.ok) this.close();
				});
				return;
			}
			if (!result.ok) {
				this.failedAction = "save";
				return;
			}
			this.close();
		} catch {
			this.failedAction = "save";
		} finally {
			this.saving = false;
		}
	}

	/**
	 * Named to avoid colliding with the inherited Element.prototype.remove().
	 *
	 * An action the system is going to refuse asks no "are you sure?" first,
	 * so a click landing while `usedByCount` already reads above zero goes
	 * straight to the refusal — the button is greyed at that count, but the
	 * catalog is live and an event can name this venue between the render
	 * and the press. Every other reference is invisible here, so the door's
	 * own census is what answers those.
	 */
	private async deleteLocation(): Promise<void> {
		if (!this.id || this.saving) return;
		const id = this.id;
		const seenEvents =
			locationsPageView.get().rows.find((r) => r.id === id)?.usedByCount ?? 0;
		if (seenEvents > 0) {
			const seen: Dependents.DependentsInput = { events: seenEvents };
			this.openDeleteRefusal(Dependents.DependentsSchema.parse(seen));
			return;
		}
		this.saving = true;
		this.activeAction = "delete";
		try {
			const ok = await confirm({
				title: this.t("admin.locations.form.deleteConfirmTitle"),
				message: this.t("admin.common.confirmCannotUndo"),
				confirmText: this.t("admin.common.delete"),
				cancelText: this.t("admin.common.cancel"),
				variant: "danger",
			});
			if (!ok) return;
			const requestId = crypto.randomUUID();
			const resultPromise = firstValueFrom(
				locationsResult$.pipe(
					filter((r) => r.requestId === requestId),
					takeUntil(this.disconnecting),
				),
			);
			locationsAction$.next({ kind: "delete", requestId, id });
			const result = await resultPromise;
			if (result.dependents) {
				this.openDeleteRefusal(result.dependents);
				return;
			}
			if (!result.ok) {
				this.failedAction = "delete";
				return;
			}
			this.close();
		} catch {
			this.failedAction = "delete";
		} finally {
			this.saving = false;
		}
	}

	private openDeleteRefusal(dependents: Dependents.Dependents): void {
		show(
			() =>
				html`<delete-refusal entity="location" .dependents=${dependents}></delete-refusal>`,
			{
				sticky: true,
				ariaLabel: this.t("admin.locations.form.deleteRefusalTitle"),
			},
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}

	override render(): TemplateResult {
		const editing = !!this.id;
		const usedByCount = editing
			? (locationsPageView.get().rows.find((r) => r.id === this.id)
					?.usedByCount ?? 0)
			: 0;
		const canSave =
			this.name.trim().length > 0 &&
			this.address.trim().length > 0 &&
			this.timezone.trim().length > 0;

		return html`
			<div class="flex h-full flex-col min-w-[min(90vw,28rem)]">
				<div ?inert=${this.saving} ${overflowWithin()} class="flex flex-col gap-4 p-4">
					<schmancy-input
						label=${this.t("admin.locations.form.nameLabel")}
						required
						.value=${this.name}
						@input=${(e: CustomEvent<{ value: string }>) => (this.name = e.detail.value)}
					></schmancy-input>

					<schmancy-place-picker
						label=${this.t("admin.locations.form.addressLabel")}
						required
						apiKey=${workspaceMapsApiKey.get() ?? ""}
						hint=${this.t("admin.locations.form.addressHint")}
						.address=${this.address}
						.latitude=${this.lat ?? undefined}
						.longitude=${this.lng ?? undefined}
						@input=${(e: SchmancyPlacePickerInputEvent) => (this.address = e.detail.value)}
						@change=${(e: SchmancyPlacePickerChangeEvent) => {
							this.address = e.detail.address;
							this.lat = e.detail.lat;
							this.lng = e.detail.lng;
							// A courteous assist, never an override — only fills a name the
							// operator hasn't already chosen one for, and only off a resolved
							// place (placeId present), not a manually-typed address.
							if (e.detail.placeId && !this.name.trim())
								this.name = e.detail.address;
						}}
					></schmancy-place-picker>
                    ${when(!workspaceMapsApiKey.get(), () => html`<schmancy-typography type="body" token="sm">${this.t("admin.site.manualAddress")}</schmancy-typography><a class="text-primary-default underline" href=${`${organizationPathPrefix.get()}/admin/settings/integrations`}>${this.t("admin.site.configureMaps")}</a>`)}

					<schmancy-autocomplete
						label=${this.t("admin.locations.form.timezoneLabel")}
						required
						.value=${this.timezone}
						hint=${this.t("admin.locations.form.timezoneHint")}
						@change=${(e: CustomEvent<{ value: string | string[] }>) =>
							(this.timezone = Array.isArray(e.detail.value)
								? ""
								: e.detail.value)}
					>
						${repeat(
							TIMEZONE_OPTIONS,
							(tz) => tz,
							(tz) =>
								html`<schmancy-option value=${tz} label=${tz}></schmancy-option>`,
						)}
					</schmancy-autocomplete>

					<schmancy-input
						label=${this.t("admin.locations.form.websiteLabel")}
						type="url"
						.value=${this.url}
						@change=${(e: CustomEvent<{ value: string }>) => (this.url = e.detail.value)}
					></schmancy-input>

					<schmancy-textarea
						label=${this.t("admin.locations.form.notesLabel")}
						autoHeight
						hint=${this.t("admin.locations.form.notesHint")}
						.value=${this.notes}
						@change=${(e: CustomEvent<{ value: string }>) => (this.notes = e.detail.value)}
					></schmancy-textarea>

					<schmancy-autocomplete
						label=${this.t("admin.locations.form.withinLabel")}
						placeholder=${this.t("admin.locations.form.withinPlaceholder")}
						.value=${this.within}
						@change=${(e: CustomEvent<{ value: string | string[] }>) =>
							(this.within = Array.isArray(e.detail.value)
								? ""
								: e.detail.value)}
					>
						<schmancy-option value="">${this.t("admin.locations.form.noParentVenue")}</schmancy-option>
						${repeat(
							withinOptions(this.id ?? null).filter((l) => l.id !== this.id),
							(l) => l.id,
							(l) =>
								html`<schmancy-option value=${l.id}>${l.name}</schmancy-option>`,
						)}
					</schmancy-autocomplete>
				</div>

				<schmancy-surface opacity="translucent" class="flex flex-wrap items-center gap-3 px-4 pb-4 pt-2">
					${when(
						editing,
						() => html`
							<div class="flex flex-col gap-1">
								<schmancy-button
									variant="outlined"
									color="error"
									?disabled=${usedByCount > 0 || (this.saving && this.activeAction !== "delete")}
									.busy=${this.saving && this.activeAction === "delete"} @click=${() => this.deleteLocation()}
								>
									<span style="display:grid"><span style=${this.failedAction === "delete" ? "grid-area:1/1;visibility:hidden" : "grid-area:1/1"} aria-hidden=${this.failedAction === "delete" ? "true" : "false"}>${this.t("admin.common.delete")}</span><span style=${this.failedAction === "delete" ? "grid-area:1/1" : "grid-area:1/1;visibility:hidden"} aria-hidden=${this.failedAction === "delete" ? "false" : "true"}>${this.t("admin.common.retryDelete")}</span></span>
								</schmancy-button>
								${when(
									usedByCount > 0,
									() => html`
										<schmancy-typography type="body" token="xs" class="text-surface-onVariant">
											${this.t(
												usedByCount === 1
													? "admin.locations.form.usedByWarningOne"
													: "admin.locations.form.usedByWarningOther",
												{ count: usedByCount },
											)}
										</schmancy-typography>
									`,
								)}
							</div>
						`,
					)}
					${when(
						this.saveUnconfirmed,
						() =>
							html`<schmancy-typography role="alert" type="body" token="sm" class="text-error-default">${this.t("admin.locations.form.saveUnconfirmed")}</schmancy-typography>`,
					)}
					<div class="ml-auto flex flex-wrap justify-end gap-2">
						<schmancy-button variant="outlined" ?disabled=${this.saving} @click=${() => this.close()}>${this.t("admin.common.cancel")}</schmancy-button>
						<schmancy-button variant="filled" ?disabled=${!canSave || (this.saving && this.activeAction !== "save")} .busy=${this.saving && this.activeAction === "save"} color=${this.failedAction === "save" ? "error" : "primary"} @click=${() => this.save()}>
							<span style="display:grid"><span style=${this.failedAction === "save" ? "grid-area:1/1;visibility:hidden" : "grid-area:1/1"} aria-hidden=${this.failedAction === "save" ? "true" : "false"}>${this.t("admin.common.save")}</span><span style=${this.failedAction === "save" ? "grid-area:1/1" : "grid-area:1/1;visibility:hidden"} aria-hidden=${this.failedAction === "save" ? "false" : "true"}>${this.t("admin.common.retrySave")}</span></span>
						</schmancy-button>
					</div>
				</schmancy-surface>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"location-form": LocationForm;
	}
}
